import React, { useEffect, useState, useRef } from 'react';
import Header from './Header.jsx';
import Footer from './Footer.jsx';
import banner from '../assets/industries/constructionindustry.jpg';
import { FiSettings, FiLayers, FiCheckCircle, FiArrowRight, FiAward } from 'react-icons/fi';
import '../css/products.css';
import p1 from '../assets/p1.jpg';
import p2 from '../assets/p2.jpg';
import p3 from '../assets/p3.jpg';
import p4 from '../assets/p4.jpg';
import p5 from '../assets/p5.jpg';
import p6 from '../assets/p6.jpg';
import p7 from '../assets/p7.jpg';

const products = [
  {
    id: 1,
    name: 'Gate Valves',
    category: 'Industrial Valves',
    image: p2,
    description: 'High-performance gate valves designed for optimal flow control with minimal pressure drop.',
    specifications: {
      range: '2" – 24"',
      pressure: 'Up to Class 2500',
      materials: 'Carbon Steel, Stainless Steel, Alloy, Duplex',
      standards: 'ASME B16.10, ASME B16.34, API 600'
    },
    features: [
      'Rising/Non-rising stem',
      'Bolted Bonnet',
      'Cryogenic options available',
      'Metal-to-metal sealing',
      'Fire-safe design options'
    ],
    icon: <FiSettings />
  },
  {
    id: 2,
    name: 'Globe Valves',
    category: 'Industrial Valves',
    image: p3,
    description: 'Precision-engineered globe valves for fine flow regulation and control in critical applications.',
    specifications: {
      range: '2" – 16"',
      pressure: 'Class 150 – 2500',
      materials: 'Carbon Steel, Stainless Steel, Alloy',
      standards: 'BS 1873, API 623'
    },
    features: [
      'Bolted Bonnet design',
      'Optional Gear operation',
      'Cryogenic applications',
      'Excellent throttling capability',
      'High-pressure ratings'
    ],
    icon: <FiLayers />
  },
  {
    id: 3,
    name: 'Swing Check Valves',
    category: 'Check Valves',
    image: p4,
    description: 'Reliable swing check valves preventing backflow and protecting system integrity.',
    specifications: {
      range: '2" – 24"',
      pressure: 'Class 150 – 2500',
      materials: 'Carbon Steel, Stainless Steel, Alloy',
      standards: 'API 6D, ASME B16.34'
    },
    features: [
      'Full-bore design',
      'Rapid response to flow reversal',
      'Low pressure drop',
      'Horizontal & vertical installation',
      'Metal-to-metal sealing'
    ],
    icon: <FiCheckCircle />
  },
  {
    id: 4,
    name: 'Dual Plate Check Valves',
    category: 'Check Valves',
    image: p5,
    description: 'Compact dual plate check valves offering superior performance with minimal space requirements.',
    specifications: {
      range: '2" – 24"',
      pressure: 'Class 150 – 600',
      materials: 'Carbon Steel, Stainless Steel, Duplex',
      standards: 'API 594, ASME B16.34'
    },
    features: [
      'Lightweight & compact design',
      'Quick closing mechanism',
      'Low pressure drop',
      'Bi-directional flow capability',
      'Easy maintenance'
    ],
    icon: <FiCheckCircle />
  },
  {
    id: 5,
    name: 'Floating Ball Valves',
    category: 'Ball Valves',
    image: p6,
    description: 'Versatile floating ball valves for on/off and throttling applications across industries.',
    specifications: {
      range: '½" – 12"',
      pressure: 'Class 150 – 600',
      materials: 'Carbon Steel, Stainless Steel, Alloy',
      standards: 'API 6D, ASME B16.34'
    },
    features: [
      'Full-bore & reduced-bore options',
      'Fire-safe design',
      'Blow-out proof stem',
      'Quarter-turn operation',
      'Bubble-tight sealing'
    ],
    icon: <FiSettings />
  },
  {
    id: 6,
    name: 'Trunnion Mounted Ball Valves',
    category: 'Ball Valves',
    image: p7,
    description: 'Heavy-duty trunnion mounted ball valves designed for high-pressure and large-diameter applications.',
    specifications: {
      range: '2" – 24"',
      pressure: 'Class 150 – 2500',
      materials: 'Carbon Steel, Stainless Steel, Duplex, Alloy',
      standards: 'API 6D, ASME B16.34'
    },
    features: [
      'High-pressure capability',
      'Reduced operating torque',
      'Fire-safe design',
      'DBB & DIB configurations',
      'Actuator ready'
    ],
    icon: <FiSettings />
  },
  {
    id: 7,
    name: 'Butterfly Valves',
    category: 'Industrial Valves',
    image: p6,
    description: 'Efficient butterfly valves with triple offset design for high-performance sealing in diverse applications.',
    specifications: {
      range: '2" – 24"',
      pressure: 'Class 150 – 300',
      materials: 'Carbon Steel, Stainless Steel, Duplex',
      standards: 'API 609, ASME B16.34'
    },
    features: [
      'Triple Offset design',
      'Metal-to-metal sealing',
      'Gearbox & Actuator options',
      'Low pressure drop',
      'Bi-directional flow'
    ],
    icon: <FiLayers />
  }
];

const categories = ['All', 'Industrial Valves', 'Check Valves', 'Ball Valves'];

const Products = () => {
  const [scrollY, setScrollY] = useState(0);
  const [selectedCategory, setSelectedCategory] = useState('All');
  const sectionsRef = useRef([]);

  useEffect(() => {
    const observerOptions = {
      threshold: 0.1,
      rootMargin: '0px 0px -50px 0px'
    };

    const observer = new IntersectionObserver((entries) => {
      entries.forEach((entry) => {
        if (entry.isIntersecting) {
          entry.target.classList.add('scroll-visible');
          observer.unobserve(entry.target);
        }
      });
    }, observerOptions);

    sectionsRef.current.forEach((section) => {
      if (section) {
        observer.observe(section);
      }
    });

    return () => {
      sectionsRef.current.forEach((section) => {
        if (section) {
          observer.unobserve(section);
        }
      });
    };
  }, []);

  useEffect(() => {
    const handleScroll = () => {
      setScrollY(window.scrollY);
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const filteredProducts = selectedCategory === 'All' 
    ? products 
    : products.filter(product => product.category === selectedCategory);

  return (
    <>
      <Header />
      <div className="products-banner">
        <img
          src={banner}
          alt="Products Banner"
          className="products-banner__img"
        />
        <div className="products-banner__overlay"></div>
        <div className="products-banner__content">
          <h2 className="products-banner__title">Our Products</h2>
          <div className="products-banner__subtitle">
            Complete range of precision industrial valves designed for various pressure and temperature ranges, 
            conforming to international standards like ASME, API, and ISO.
          </div>
        </div>
      </div>

      <div 
        ref={(el) => (sectionsRef.current[0] = el)}
        className="scroll-section"
      >
        <section className="products-section">
          <div className="container">
            <div className="products-header">
              <span className="products-label">Our Portfolio</span>
              <h2 className="products-title">Product Range</h2>
              <p className="products-subtitle">
                Premium quality valves engineered for reliability, performance, and long-lasting operation 
                across diverse industrial applications.
              </p>
            </div>

            <div className="products-categories">
              {categories.map((category) => (
                <button
                  key={category}
                  className={`category-btn ${selectedCategory === category ? 'active' : ''}`}
                  onClick={() => setSelectedCategory(category)}
                >
                  {category}
                </button>
              ))}
            </div>

            <div className="products-grid">
              {filteredProducts.map((product, index) => (
                <div 
                  key={product.id}
                  className="product-card"
                  style={{ animationDelay: `${index * 0.1}s` }}
                >
                  <div className="product-image-container">
                    <img 
                      src={product.image} 
                      alt={product.name}
                      className="product-image"
                      loading="lazy"
                    />
                    <div className="product-overlay">
                      <div className="product-icon">
                        {product.icon}
                      </div>
                    </div>
                    <div className="product-category-badge">{product.category}</div>
                  </div>
                  <div className="product-content">
                    <h3 className="product-name">{product.name}</h3>
                    <p className="product-description">{product.description}</p>
                    
                    <div className="product-specifications">
                      <h4 className="specs-title">Specifications</h4>
                      <div className="specs-grid">
                        <div className="spec-item">
                          <span className="spec-label">Size Range:</span>
                          <span className="spec-value">{product.specifications.range}</span>
                        </div>
                        <div className="spec-item">
                          <span className="spec-label">Pressure:</span>
                          <span className="spec-value">{product.specifications.pressure}</span>
                        </div>
                        <div className="spec-item">
                          <span className="spec-label">Materials:</span>
                          <span className="spec-value">{product.specifications.materials}</span>
                        </div>
                        <div className="spec-item">
                          <span className="spec-label">Standards:</span>
                          <span className="spec-value">{product.specifications.standards}</span>
                        </div>
                      </div>
                    </div>

                    <div className="product-features">
                      <h4 className="features-title">Key Features</h4>
                      <ul className="features-list">
                        {product.features.map((feature, idx) => (
                          <li key={idx} className="feature-item">
                            <FiCheckCircle className="feature-icon" />
                            <span>{feature}</span>
                          </li>
                        ))}
                      </ul>
                    </div>

                    <button className="product-inquiry-btn">
                      Request Quote
                      <FiArrowRight className="btn-icon" />
                    </button>
                  </div>
                  <div className="product-line"></div>
                </div>
              ))}
            </div>
          </div>
        </section>
      </div>

      <Footer />
      {scrollY > 300 && (
        <button 
          className="scroll-to-top"
          onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })}
          style={{
            position: 'fixed',
            bottom: '2rem',
            right: '2rem',
            background: '#dc2626',
            color: 'white',
            border: 'none',
            borderRadius: '50%',
            width: '50px',
            height: '50px',
            cursor: 'pointer',
            fontSize: '1.2rem',
            zIndex: 1000,
            boxShadow: '0 4px 12px rgba(220, 38, 38, 0.3)',
            transition: 'all 0.3s ease'
          }}
        >
          ↑
        </button>
      )}
    </>
  );
};

export default Products;

